/**
 * Subscription / RevenueCat client helpers.
 *
 * - `paywallUrl()` builds the RC-hosted Web Paywall Link for the current user
 *   (no secret involved — the token is a public link token from config).
 * - `manageUrl()` asks our backend for an authenticated customer-portal URL
 *   (the secret v2 key stays server-side; identity is derived from the JWT).
 */
import { authService } from '../auth/auth-service.js'
import { environment } from '../../environments/environment.prod.js'

let cachedToken: string | undefined

/** Public RC paywall link token, read once from /config.json. */
async function paywallToken(): Promise<string> {
  if (cachedToken !== undefined) return cachedToken
  try {
    const res = await fetch('/config.json')
    const cfg = (await res.json()) as { revenueCatPaywallToken?: string }
    cachedToken = cfg.revenueCatPaywallToken ?? ''
  } catch {
    cachedToken = ''
  }
  return cachedToken
}

/**
 * Build the Web Paywall Link for the signed-in user:
 *   https://pay.rev.cat/<token>/<sub>?email=<email>
 * The App User ID (Cognito sub) MUST be appended or the link won't work.
 * Returns null if we're missing the token or the user id.
 */
export async function paywallUrl(): Promise<string | null> {
  const token = await paywallToken()
  const state = authService.getState()
  const sub = state.userId
  if (!token || !sub) return null
  let url = `https://pay.rev.cat/${token}/${encodeURIComponent(sub)}`
  if (state.email) url += `?email=${encodeURIComponent(state.email)}`
  return url
}

export type ManageUrlResult =
  | { ok: true; url: string }
  | { ok: false; reason: 'unauthenticated' | 'no_subscription' | 'error' }

function requestManageUrl(token: string): Promise<Response> {
  return fetch(`${environment.api.baseUrl}/api/subscription/manage-url`, {
    method: 'GET',
    headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
  })
}

/** Fetch an authenticated customer-portal URL from our backend. */
export async function manageUrl(): Promise<ManageUrlResult> {
  const token = authService.getAccessToken()
  if (!token) return { ok: false, reason: 'unauthenticated' }
  try {
    let res = await requestManageUrl(token)
    // A 401 means the access token was stale: the background refresh timer can lag
    // after the app has been backgrounded or the device slept (setTimeout is
    // throttled/paused there), so the in-memory token may have already expired.
    // Force a refresh and retry once before surfacing an error.
    if (res.status === 401) {
      await authService.refreshSession()
      const fresh = authService.getAccessToken()
      if (fresh) res = await requestManageUrl(fresh)
    }
    if (res.status === 404) return { ok: false, reason: 'no_subscription' }
    if (!res.ok) return { ok: false, reason: 'error' }
    const data = (await res.json()) as { url?: string }
    return data.url ? { ok: true, url: data.url } : { ok: false, reason: 'error' }
  } catch {
    return { ok: false, reason: 'error' }
  }
}

/**
 * Open an external URL. On native (Capacitor) uses the in-app browser; on web,
 * navigates in-place (for checkout, so the success redirect returns here) or in
 * a new tab (for the portal, so the app stays open).
 */
export async function openExternal(url: string, newTab = false): Promise<void> {
  try {
    const { Capacitor } = await import('@capacitor/core')
    if (Capacitor.isNativePlatform()) {
      const { Browser } = await import('@capacitor/browser')
      await Browser.open({ url })
      return
    }
  } catch {
    /* not running under Capacitor — fall through to web */
  }
  if (newTab) window.open(url, '_blank', 'noopener')
  else window.location.href = url
}
